import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, Eye, Flag, Phone, ShieldCheck, Clock, CreditCard } from "lucide-react";
import { useSeo } from "@/lib/seo";

const SIGNALS = [
  {
    icon: BadgeCheck,
    title: "How we verify",
    text: "Every advertiser completes an identity document check and a live selfie before publishing, and re-verifies with a selfie every 90 days. Each profile shows how many days ago it was last verified. We never see or store the documents themselves - only the result, date, provider reference and that the person is over 18.",
  },
  {
    icon: Eye,
    title: "What we moderate",
    text: "Nothing goes live without a human review. Every new profile, every edit, every photo and every story is reviewed by a moderator. Automatic checks sort the queue by risk; they never ban anyone on their own.",
  },
  {
    icon: Clock,
    title: "What LUX will never do",
    text: "LUX does not take payments, deposits or bookings. There is no messaging on LUX. If someone asks you to pay \u201Cthrough LUX\u201D, they are attempting fraud - report the profile and we will investigate.",
  },
];

const CHECKLIST = [
  "Share your plan with a friend or family member and agree a check-in time.",
  "Call or video-call the person before meeting; confirm the voice and face match the profile.",
  "Meet first in a public place when possible; trust your instincts and leave if anything feels off.",
  "Keep your phone charged and location sharing enabled with your trusted contact.",
  "Never pay deposits or send money to someone you have not met and verified.",
];

const RED_FLAGS = [
  "Requests for a deposit, gift card or crypto before meeting.",
  "Pressure to move the conversation to a third-party booking site.",
  "Someone else answering messages or arranging meetings on their behalf.",
  "Photos that look like studio shoots or appear elsewhere on the internet.",
  "Any suggestion that the person may be under 18 or working against their will.",
];

export default function SafetyPage() {
  useSeo({
    title: "Safety guidance | LUX",
    description: "How LUX verifies advertisers, moderates content and what visitors can do to stay safe. Report concerns anonymously.",
  });
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-testid="safety-page">
      <section className="pb-10 pt-14 sm:pt-20">
        <p className="lux-label">Safety centre</p>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] sm:text-6xl" data-testid="safety-title">
          Discretion isn't the same as safety.<br />
          <em className="font-light text-lux-silverBright">Here's how LUX protects both.</em>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-lux-text/80">
          LUX is a prototype in user testing. The guidance below reflects how the service works today - who is verified, how content is reviewed, and what you can do if something feels wrong.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-3" aria-label="How LUX works">
        {SIGNALS.map(({ icon: Icon, title, text }) => (
          <article key={title} className="lux-card p-6" data-testid={`safety-pillar-${title.toLowerCase().replace(/\W+/g, "-")}`}>
            <Icon className="h-5 w-5 text-lux-silver" aria-hidden="true" />
            <h2 className="mt-5 font-serif text-2xl leading-tight">{title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-lux-muted">{text}</p>
          </article>
        ))}
      </section>

      <section className="mt-16 lux-prose" aria-labelledby="safety-checklist-h">
        <h2 id="safety-checklist-h" className="!mt-0">Before you meet or contact anyone</h2>
        <ul data-testid="safety-checklist">
          {CHECKLIST.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="red-flags-h">
        <div className="lux-card border-lux-warning/35 bg-lux-warning/5 p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-lux-warning" aria-hidden="true" />
            <div>
              <h2 id="red-flags-h" className="font-serif text-2xl text-lux-text">Red flags: stop and report</h2>
              <ul className="mt-4 space-y-2 text-[15px] leading-relaxed text-lux-text/90" data-testid="safety-red-flags">
                {RED_FLAGS.map((r) => (
                  <li key={r} className="flex gap-3">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lux-warning" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm text-lux-muted">
                Reports are anonymous and go to our trained moderators. Anything about a possible minor or coercion is routed straight to Compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14 grid gap-4 sm:grid-cols-2">
        <a
          href="tel:08000121700"
          className="lux-card group block p-6 transition-colors hover:border-lux-silver/55"
          data-testid="safety-helpline"
        >
          <Phone className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 text-xs text-lux-muted">Free, confidential, 24 hours</p>
          <p className="mt-1 font-serif text-3xl text-lux-silverBright">08000 121 700</p>
          <p className="mt-2 text-sm text-lux-muted">Modern Slavery &amp; Exploitation Helpline. In an emergency call 999.</p>
        </a>
        <Link
          to="/report"
          className="lux-card group block p-6 transition-colors hover:border-lux-silver/55"
          data-testid="safety-report-cta"
        >
          <Flag className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 text-xs text-lux-muted">No account needed</p>
          <p className="mt-1 font-serif text-3xl text-lux-silverBright">Report a concern</p>
          <p className="mt-2 text-sm text-lux-muted">Tell us about a profile, story or interaction. Reports are reviewed within 24 hours.</p>
        </Link>
      </section>

      <section className="mt-14 lux-card flex items-start gap-4 p-6 sm:p-8">
        <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-lux-silver" aria-hidden="true" />
        <div>
          <h2 className="font-serif text-2xl text-lux-text">A note on payments</h2>
          <p className="mt-2 text-sm leading-relaxed text-lux-muted">
            LUX never collects money from visitors. Any deposit request through \u201CLUX\u201D or by bank transfer to an unknown account is a scam. Decline it and <Link to="/report" className="lux-link">report the profile</Link>.
          </p>
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-lux-muted">
            <CreditCard className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" /> We do not use third-party analytics or trackers.
          </p>
        </div>
      </section>
    </div>
  );
}
